import { createMuiTheme, createStyles, Theme, withStyles } from '@material-ui/core/styles';
import createBreakpoints from '@material-ui/core/styles/createBreakpoints';
import { Tooltip } from '@material-ui/core';
import { DeltaXMLColors, DeltaJSONColors } from './DeltaColors';

export class Themes {

  public static breakpoints = createBreakpoints({})
  public static theme = createMuiTheme();

  public static appLogoDimensions = {
    height: '32px',
    width: '150px'
  };

  public static HtmlTooltip = withStyles((theme: Theme) => ({
    tooltip: {
      maxWidth: 320,
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: '20px',
      fontFamily: ['open-sans', 'HelveticaNeue', 'Helvetica Neue', 'Helvetica-Neue', 'Helvetica', 'Arial', 'sans-serif'].join(','),
      color: DeltaJSONColors.WHITE,
      backgroundColor: DeltaXMLColors.OXFORD_BLUE,
      borderRadius: 4
    },
    arrow: {
      color: DeltaXMLColors.OXFORD_BLUE
    },
  }))(Tooltip);

  public static createPaperBaseStyles(theme: Theme, drawerWidth: number) {

    return createStyles({
      root: {
        display: 'flex',
        minHeight: '100vh',
      },
      drawer: {
        [theme.breakpoints.up('sm')]: {
          width: drawerWidth,
          flexShrink: 0,
        },
      },
      app: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
      },
      main: {
        flex: 1,
        padding: theme.spacing(3, 3),
        background: DeltaXMLColors.LIGHT_BACKGROUND,
      },
      main2: {
        flex: 1,
        padding: theme.spacing(0, 0),
        background: DeltaXMLColors.LIGHT_BACKGROUND,
      },
      footer: {
        padding: theme.spacing(2),
        background: DeltaXMLColors.LIGHT_BACKGROUND,
      },
    });
  }

  public static MuiTheme = createMuiTheme({
    palette: {
      primary: {
        light: DeltaXMLColors.CELERY,
        main: DeltaXMLColors.OXFORD_BLUE,
        dark: DeltaXMLColors.CELERY,
      },
      secondary: {
        light: DeltaXMLColors.CELERY,
        main: DeltaXMLColors.GREY,
        dark: DeltaXMLColors.CELERY,
      }
    },
    typography: {
      fontFamily: ['open-sans', 'HelveticaNeue', 'Helvetica Neue', 'Helvetica-Neue', 'Helvetica', 'Arial', 'sans-serif'].join(','),
      h1: {
        fontSize: '55px',
        fontWeight: 600,
        lineHeight: '67px'
      },
      h2: {
        fontSize: '40px',
        fontWeight: 600,
        lineHeight: '50px'
      },
      h3: {
        fontSize: '28px',
        fontWeight: 500,
        lineHeight: '39px'
      },
      h4: {
        fontSize: '22px',
        fontWeight: 500,
        lineHeight: '30px'
      },
      h5: {
        fontSize: '20px',
        fontWeight: 400,
        lineHeight: '30px'
      },
      h6: {
        fontSize: '17px',
        fontWeight: 400,
        lineHeight: '25px',
      },
      body1: {
        fontSize: '17px',
        fontWeight: 400,
        lineHeight: '25px',
        fontFamily: ['open-sans', 'HelveticaNeue', 'Helvetica Neue', 'Helvetica-Neue', 'Helvetica', 'Arial', 'sans-serif'].join(',')
      },
      body2: {
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: '20px',
        fontFamily: ['open-sans', 'HelveticaNeue', 'Helvetica Neue', 'Helvetica-Neue', 'Helvetica', 'Arial', 'sans-serif'].join(',')
      }
    },
    shape: {
      borderRadius: 8,
    },
    props: {
      MuiTab: {
        disableFocusRipple: true,
      },
    },
    mixins: {
      toolbar: {
        minHeight: 48,
      },
    },
    overrides: {
      MuiDrawer: {
        paper: {
          backgroundColor: DeltaXMLColors.DARK_BACKGROUND,
        }
      },
      MuiButton: {
        root: {
          borderRadius: 'none'
        },
        label: {
          textTransform: 'none',
        },
        contained: {
          borderRadius: '0',
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: '20px',
          fontFamily: ['open-sans', 'HelveticaNeue', 'Helvetica Neue', 'Helvetica-Neue', 'Helvetica', 'Arial', 'sans-serif'].join(','),
          boxShadow: 'none',
          '&:active': {
            boxShadow: 'none',
          }
        },
        containedSecondary: {
          backgroundColor: 'transparent',
          border: '1px solid',
          '&:hover': {
            // Set hover color
            backgroundColor: DeltaXMLColors.CELERY,
            border: '1px solid ' + DeltaXMLColors.CELERY,
            boxShadow: 'none'
          }
        },
        containedPrimary: {
          backgroundColor: 'transparent',
          color: DeltaXMLColors.OXFORD_BLUE,
          border: '1px solid',
          '&:hover': {
            // Set hover color
            backgroundColor: DeltaXMLColors.OXFORD_BLUE,
            color: DeltaJSONColors.WHITE,
            border: '1px solid ' + DeltaXMLColors.OXFORD_BLUE,
            boxShadow: 'none'
          }
        }
      },
      MuiTabs: {
        root: {
          marginLeft: Themes.theme.spacing(1),
        },
        indicator: {
          height: 3,
          borderTopLeftRadius: 3,
          borderTopRightRadius: 3,
          backgroundColor: DeltaXMLColors.CELERY,
        },
      },
      MuiTab: {
        root: {
          textTransform: 'none',
          margin: '0 16px',
          minWidth: 0,
          padding: 0,
          [Themes.breakpoints.up('md')]: {
            padding: 0,
            minWidth: 0,
          },
          '&$selected': {
            color: DeltaXMLColors.CELERY,
            fontWeight: 600
          },
        },
        textColorInherit: {
          fontWeight: 600,
          fontSize: '14px',
          lineHeight: '20px',
          opacity: 'none',
          color: DeltaJSONColors.WHITE,
        }
      },
      MuiIconButton: {
        root: {
          padding: Themes.theme.spacing(1),
        },
      },
      MuiListItemText: {
        primary: {
          fontWeight: 600,
          fontSize: '14px',
          lineHeight: '20px',
          fontFamily: ['open-sans', 'HelveticaNeue', 'Helvetica Neue', 'Helvetica-Neue', 'Helvetica', 'Arial', 'sans-serif'].join(','),
        },
      },
      MuiListItemIcon: {
        root: {
          color: 'inherit',
          marginRight: 0,
          '& svg': {
            fontSize: 20,
          },
        },
      },
      MuiAvatar: {
        root: {
          width: 32,
          height: 32,
        },
      },
      //for jobs history selected row
      MuiTableRow: {
        root: {
          '&$selected, &$selected:hover': {
            backgroundColor: DeltaJSONColors.MERCURY
          }
        }
      },
      MuiTableCell: {
        root: {
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: '20px'
        },
        body: {
          color: DeltaXMLColors.OXFORD_BLUE
        }
      },
      MuiRadio: {
        colorPrimary: {
          color: DeltaJSONColors.WHITE,
          '&$checked': {
            color: DeltaXMLColors.CELERY
          }
        },
        colorSecondary: {
          color: DeltaXMLColors.OXFORD_BLUE,
          '&$checked': {
            color: DeltaXMLColors.CELERY
          }
        }

      },
      MuiInputBase: {
        root: {
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: '20px',
          color: DeltaXMLColors.OXFORD_BLUE,
          fontFamily: ['open-sans', 'HelveticaNeue', 'Helvetica Neue', 'Helvetica-Neue', 'Helvetica', 'Arial', 'sans-serif'].join(',')
        },
        input: {
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: '20px',
          color: DeltaXMLColors.OXFORD_BLUE,
          fontFamily: ['open-sans', 'HelveticaNeue', 'Helvetica Neue', 'Helvetica-Neue', 'Helvetica', 'Arial', 'sans-serif'].join(',')
        }

      },
      MuiFormControlLabel: {
        label: {
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: '20px',
          fontFamily: ['open-sans', 'HelveticaNeue', 'Helvetica Neue', 'Helvetica-Neue', 'Helvetica', 'Arial', 'sans-serif'].join(',')
        },

      },
      MuiFormLabel: {
        root: {
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: '20px',
          color: DeltaXMLColors.OXFORD_BLUE,
          fontFamily: ['open-sans', 'HelveticaNeue', 'Helvetica Neue', 'Helvetica-Neue', 'Helvetica', 'Arial', 'sans-serif'].join(',')
        }
      },
      MuiSwitch: {
        colorPrimary: {
          '&$checked': {
            color: DeltaXMLColors.CELERY
          }
        }
      },
      MuiList: {
        padding: {
          paddingTop: '0px',
          paddingBottom: '0px'
        }
      }
    },

  });
}